/*
 * <variant-gate> — forces an explicit size + colour choice before purchase (SHISHA).
 *
 * Wraps the PDP <product-form>. On load it clears any pre-selected variant so nothing
 * is chosen, then keeps BOTH the "Add to cart" and dynamic-checkout ("Buy it now")
 * buttons disabled/greyed — with an on-brand helper message — until the shopper has
 * actively picked every option (size AND colour).
 *
 * It cooperates with Dawn's product-info round-trip rather than replacing it:
 *  - Tracks which option groups the shopper has explicitly chosen.
 *  - Strips the auto-selection Shopify applies to not-yet-chosen options on re-render
 *    (via product-info's preprocess hook), so an un-picked option never looks chosen.
 *  - Only trusts the form's variant id once all options are chosen and the exact
 *    variant has resolved, so Add to cart / Buy it now always submit the right variant.
 *  - Defers to Dawn's sold-out / unavailable state for the resolved combination.
 */
if (!customElements.get('variant-gate')) {
  customElements.define(
    'variant-gate',
    class VariantGate extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;
        this.sectionId = this.productInfo.dataset.section;

        const variantSelects = this.getVariantSelects();
        // Single-variant product (no options to choose) — leave the normal buy flow alone.
        if (!variantSelects) {
          this.applicable = false;
          return;
        }

        this.applicable = true;
        this.selectedGroups = new Set();
        this.pending = false;

        this.clearInitialSelection(variantSelects);

        // Strip auto-selection of not-yet-chosen options out of server re-renders.
        if (typeof this.productInfo.addPreProcessCallback === 'function' && !this.productInfo._variantGateHooked) {
          this.productInfo._variantGateHooked = true;
          this.productInfo.addPreProcessCallback((node) => this.stripUnchosenOptions(node));
        }

        // Delegated: the <variant-selects> element is replaced on every update, but
        // <product-info> persists, so listen there.
        this._onChange = (event) => this.handleOptionChange(event);
        this.productInfo.addEventListener('change', this._onChange);

        // Re-evaluate once Dawn has resolved the exact variant.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            this.pending = false;
            this.render();
          });
        }

        // Hard stop: never allow a submit while the selection is incomplete
        // (covers Enter key or any programmatic submit).
        this._form = this.querySelector('form');
        if (this._form) {
          this._onSubmit = (event) => {
            if (!this.isComplete()) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.render();
            }
          };
          this._form.addEventListener('submit', this._onSubmit, true);
        }

        this.render();
      }

      disconnectedCallback() {
        if (this._onChange && this.productInfo) this.productInfo.removeEventListener('change', this._onChange);
        if (this._onSubmit && this._form) this._form.removeEventListener('submit', this._onSubmit, true);
        this._unsubscribe?.();
      }

      getVariantSelects() {
        return this.productInfo ? this.productInfo.querySelector('variant-selects') : null;
      }

      getOptionGroups() {
        const variantSelects = this.getVariantSelects();
        return variantSelects ? Array.from(variantSelects.querySelectorAll('.product-form__input')) : [];
      }

      get idInput() {
        return this.querySelector('input[name="id"]');
      }

      get submitButton() {
        return this.querySelector('.product-form__submit');
      }

      get expressWrapper() {
        return this.querySelector('.product-form__express');
      }

      get message() {
        return this.querySelector('.variant-gate__message');
      }

      isComplete() {
        const total = this.getOptionGroups().length;
        return total > 0 && this.selectedGroups.size >= total;
      }

      clearInitialSelection(variantSelects) {
        variantSelects.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });
        variantSelects.querySelectorAll('[data-selected-value]').forEach((el) => {
          el.textContent = '';
        });
        const idInput = this.idInput;
        if (idInput) {
          idInput.value = '';
          idInput.disabled = true;
        }
      }

      handleOptionChange(event) {
        const input = event.target.closest('input[type="radio"]');
        if (!input || !input.closest('variant-selects')) return;

        const group = input.closest('.product-form__input');
        const index = this.getOptionGroups().indexOf(group);
        if (index > -1) this.selectedGroups.add(index);

        // A round-trip is about to resolve the exact variant. Keep everything gated
        // until it lands so a stale variant id can never be submitted mid-update.
        this.pending = true;
        this.render();
      }

      stripUnchosenOptions(node) {
        if (!node || typeof node.querySelectorAll !== 'function') return;
        Array.from(node.querySelectorAll('.product-form__input')).forEach((group, index) => {
          if (this.selectedGroups.has(index)) return;
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = false;
            input.removeAttribute('checked');
          });
          group.querySelectorAll('[data-selected-value]').forEach((el) => {
            el.textContent = '';
          });
        });
      }

      render() {
        if (!this.applicable) return;

        if (!this.isComplete()) {
          this.applyGated(true);
          return;
        }
        if (this.pending) {
          // Everything chosen, but the exact variant is still resolving.
          this.applyGated(false);
          return;
        }

        // Complete and resolved — defer to Dawn's availability decision.
        if (this.submitButton?.hasAttribute('disabled')) {
          this.applySoldOut();
        } else {
          this.applyActive();
        }
      }

      applyGated(showMessage) {
        const button = this.submitButton;
        if (button) {
          button.setAttribute('disabled', 'disabled');
          button.dataset.gateDisabled = 'true';
        }
        const idInput = this.idInput;
        if (idInput) {
          idInput.value = '';
          idInput.disabled = true;
        }
        this.setExpressDisabled(true);
        this.toggleMessage(showMessage);
      }

      applyActive() {
        const button = this.submitButton;
        if (button && button.dataset.gateDisabled) {
          button.removeAttribute('disabled');
          delete button.dataset.gateDisabled;
        }
        const idInput = this.idInput;
        if (idInput) idInput.disabled = false;
        this.setExpressDisabled(false);
        this.toggleMessage(false);
      }

      applySoldOut() {
        // Dawn keeps the submit button disabled with a "Sold out" / "Unavailable" label.
        if (this.submitButton) delete this.submitButton.dataset.gateDisabled;
        this.setExpressDisabled(true);
        this.toggleMessage(false);
      }

      setExpressDisabled(disabled) {
        const wrapper = this.expressWrapper;
        if (!wrapper) return;
        wrapper.classList.toggle('variant-gate--disabled', disabled);
        if (disabled) {
          wrapper.setAttribute('inert', '');
          wrapper.setAttribute('aria-disabled', 'true');
        } else {
          wrapper.removeAttribute('inert');
          wrapper.removeAttribute('aria-disabled');
        }
      }

      toggleMessage(show) {
        const message = this.message;
        if (message) message.toggleAttribute('hidden', !show);
      }
    }
  );
}
